/**
 * ONE TAP — synthesis toolkit.
 *
 * Everything in ONE TAP's soundtrack and sound effects is generated here at runtime:
 * no sample files, no fetch, no base64. This module is the low level layer — oscillators
 * with real ADSR, noise generators, filter chains with envelopes, waveshapers, FM and
 * ring modulation, Karplus-Strong metal, transient clicks and pitch-envelope helpers.
 *
 * Design rules followed throughout:
 *  - Every AudioParam ramp that ends at zero uses an exponential ramp to MIN_GAIN and then
 *    a hard set, because `exponentialRampToValueAtTime(0)` throws.
 *  - Buffers are generated with the shared deterministic RNG so a given sound has the same
 *    grain every session. Audio that changes character run to run reads as a bug.
 *  - Buffers are cached aggressively. Generating noise or a Karplus string during a firefight
 *    is a main-thread stall, and this game runs at 144fps.
 */

import { clamp, clamp01, hashString, lerpN, makeRng } from '@shared/math';

export type Rng = () => number;

/** Exponential ramps cannot reach zero; this is our practical silence. */
export const MIN_GAIN = 1e-4;

export const dbToGain = (db: number): number => Math.pow(10, db / 20);
export const gainToDb = (g: number): number => 20 * Math.log10(Math.max(g, MIN_GAIN));

/** Frequency ratio for a number of semitones. */
export const ratio = (semis: number): number => Math.pow(2, semis / 12);

/** Note frequency, in semitones above A1 (55 Hz) — the game's musical origin. */
export const noteHz = (semisAboveA1: number): number => 55 * Math.pow(2, semisAboveA1 / 12);

export const randRange = (rng: Rng, a: number, b: number): number => a + (b - a) * rng();
export const randSign = (rng: Rng): number => (rng() < 0.5 ? -1 : 1);
export const pick = <T>(rng: Rng, arr: readonly T[]): T => arr[Math.min(arr.length - 1, (rng() * arr.length) | 0)]!;

/**
 * Symmetric multiplicative jitter: `jitter(rng, 0.03)` returns a factor in [0.97, 1.03].
 *
 * Identical repeats are the single clearest tell of synthesised audio (DESIGN.md §5.5), so
 * every pitch, cutoff and layer balance in the kit is multiplied by one of these. The rng is
 * the shared deterministic stream, never `Math.random` — a session must be reproducible.
 */
export const jitter = (rng: Rng, amount: number): number => 1 + (rng() * 2 - 1) * amount;

// ---------------------------------------------------------------------------
// Param helpers
// ---------------------------------------------------------------------------

/** Exponential ramp that is safe when the target is zero or negative. */
export function expTo(p: AudioParam, target: number, time: number): void {
  p.exponentialRampToValueAtTime(target > MIN_GAIN ? target : MIN_GAIN, time);
}

/** Ramp a gain param down to silence and pin it there so it cannot creep back up. */
export function fadeOut(p: AudioParam, t0: number, seconds: number): number {
  const end = t0 + Math.max(seconds, 0.002);
  p.cancelScheduledValues(t0);
  p.setValueAtTime(Math.max(p.value, MIN_GAIN), t0);
  p.exponentialRampToValueAtTime(MIN_GAIN, end);
  p.setValueAtTime(0, end + 0.001);
  return end + 0.001;
}

export interface ADSR {
  /** Attack seconds. */
  a: number;
  /** Decay seconds. */
  d: number;
  /** Sustain level, 0..1 of peak. */
  s: number;
  /** Release seconds. */
  r: number;
}

/**
 * Schedule a full ADSR on a gain param. `gate` is how long the note is held (attack+decay+
 * sustain); release happens after it. Returns the absolute time the envelope finishes.
 */
export function applyADSR(param: AudioParam, t0: number, env: ADSR, gate: number, peak = 1): number {
  const a = Math.max(env.a, 0.0006);
  const d = Math.max(env.d, 0.0008);
  const s = clamp01(env.s) * peak;
  const r = Math.max(env.r, 0.004);
  const hold = Math.max(gate, a + d);

  param.cancelScheduledValues(t0);
  param.setValueAtTime(MIN_GAIN, t0);
  param.exponentialRampToValueAtTime(Math.max(peak, MIN_GAIN), t0 + a);
  param.exponentialRampToValueAtTime(Math.max(s, MIN_GAIN), t0 + a + d);
  param.setValueAtTime(Math.max(s, MIN_GAIN), t0 + hold);
  param.exponentialRampToValueAtTime(MIN_GAIN, t0 + hold + r);
  param.setValueAtTime(0, t0 + hold + r + 0.001);
  return t0 + hold + r + 0.001;
}

/**
 * Attack times at or below this are scheduled as a hard step instead of a ramp.
 *
 * This threshold is the single most consequential number in the whole audio layer, and
 * getting it wrong is why the game's impacts read as soft. Every transient in ONE TAP is a
 * `ClickFactory` buffer whose entire energy is in **sample zero** (`d[0] = 1`, then an
 * `exp(-i·21.6/n)` decay). Ramping a gain from MIN_GAIN (1e-4) up to peak over even 0.4 ms
 * multiplies that loudest sample by ~0.0001 and then fades the click in *across its own
 * decay*: the layers are all present, and the envelope eats them before the ear arrives.
 *
 * A step is safe because every source in this kit is `start()`ed at the same instant its
 * envelope begins — the signal before `t0` is silence, so there is no discontinuity to
 * click on. Below ~1 ms a ramp buys nothing but softness.
 */
const SNAP_ATTACK = 0.001;

/**
 * Percussive envelope: near-instant attack into an exponential tail. This is the shape
 * behind almost every impact in the game.
 *
 * `attack <= SNAP_ATTACK` (including 0) schedules the peak as a step. See `SNAP_ATTACK`.
 */
export function percEnv(param: AudioParam, t0: number, attack: number, decay: number, peak = 1, hold = 0): number {
  const p = Math.max(peak, MIN_GAIN);
  param.cancelScheduledValues(t0);

  if (attack <= SNAP_ATTACK) {
    // Hard step. This is what makes an impact arrive rather than begin.
    param.setValueAtTime(p, t0);
    const end = t0 + hold + Math.max(decay, 0.004);
    if (hold > 0) param.setValueAtTime(p, t0 + hold);
    param.exponentialRampToValueAtTime(MIN_GAIN, end);
    param.setValueAtTime(0, end + 0.001);
    return end + 0.001;
  }

  param.setValueAtTime(MIN_GAIN, t0);
  param.linearRampToValueAtTime(p, t0 + attack);
  if (hold > 0) param.setValueAtTime(p, t0 + attack + hold);
  param.exponentialRampToValueAtTime(MIN_GAIN, t0 + attack + hold + Math.max(decay, 0.004));
  param.setValueAtTime(0, t0 + attack + hold + Math.max(decay, 0.004) + 0.001);
  return t0 + attack + hold + Math.max(decay, 0.004) + 0.001;
}

/**
 * Explicit zero-attack percussive envelope. Identical to `percEnv(param, t0, 0, ...)` but
 * says what it means at the call site — new impact code should use this.
 */
export function snapEnv(param: AudioParam, t0: number, decay: number, peak = 1, hold = 0): number {
  return percEnv(param, t0, 0, decay, peak, hold);
}

/**
 * A swell that rises INTO a moment and is gone by the time it arrives.
 *
 * The reverse of `percEnv`, and the reason a throw reads as effort rather than as a click:
 * the ear hears something being wound up, and the release lands in a hole the swell dug.
 */
export function swellEnv(param: AudioParam, t0: number, rise: number, peak = 1, fall = 0.02): number {
  const p = Math.max(peak, MIN_GAIN);
  const end = t0 + Math.max(rise, 0.004) + Math.max(fall, 0.004);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(MIN_GAIN, t0);
  param.exponentialRampToValueAtTime(p, t0 + Math.max(rise, 0.004));
  param.exponentialRampToValueAtTime(MIN_GAIN, end);
  param.setValueAtTime(0, end + 0.001);
  return end + 0.001;
}

/**
 * Pitch envelope. `shape` < 1 falls fast then flattens (a drum), > 1 lingers high then
 * plunges. Everything with a "thump" in this game uses one of these.
 */
export function pitchEnv(param: AudioParam, t0: number, from: number, to: number, seconds: number, shape = 1): void {
  param.cancelScheduledValues(t0);
  param.setValueAtTime(Math.max(from, 0.01), t0);
  if (Math.abs(shape - 1) < 0.01) {
    param.exponentialRampToValueAtTime(Math.max(to, 0.01), t0 + Math.max(seconds, 0.004));
    return;
  }
  // Hand-stepped curve for non-exponential shapes.
  const steps = 24;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const w = Math.pow(t, shape);
    param.exponentialRampToValueAtTime(Math.max(lerpN(from, to, w), 0.01), t0 + seconds * t);
  }
}

/** Linear sweep on any param (filter cutoff, detune, Q...). */
export function sweep(param: AudioParam, t0: number, from: number, to: number, seconds: number, exponential = true): void {
  param.cancelScheduledValues(t0);
  param.setValueAtTime(from, t0);
  if (exponential && from > 0 && to > 0) param.exponentialRampToValueAtTime(to, t0 + Math.max(seconds, 0.004));
  else param.linearRampToValueAtTime(to, t0 + Math.max(seconds, 0.004));
}

// ---------------------------------------------------------------------------
// Node factories
// ---------------------------------------------------------------------------

export function gain(ctx: BaseAudioContext, value = 1): GainNode {
  const g = ctx.createGain();
  g.gain.value = value;
  return g;
}

export function biquad(
  ctx: BaseAudioContext,
  type: BiquadFilterType,
  freq: number,
  q = 0.7071,
  gainDb = 0,
): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = clamp(freq, 10, ctx.sampleRate * 0.49);
  f.Q.value = q;
  f.gain.value = gainDb;
  return f;
}

export interface FilterSpec {
  type: BiquadFilterType;
  freq: number;
  q?: number;
  gainDb?: number;
}

export interface FilterChain {
  input: BiquadFilterNode;
  output: BiquadFilterNode;
  filters: BiquadFilterNode[];
}

/** Build and wire a series chain of biquads. Returns the individual filters so you can automate them. */
export function filterChain(ctx: BaseAudioContext, specs: FilterSpec[]): FilterChain {
  const filters = specs.map((s) => biquad(ctx, s.type, s.freq, s.q ?? 0.7071, s.gainDb ?? 0));
  for (let i = 0; i < filters.length - 1; i++) filters[i]!.connect(filters[i + 1]!);
  return { input: filters[0]!, output: filters[filters.length - 1]!, filters };
}

export function osc(ctx: BaseAudioContext, type: OscillatorType, freq: number, detuneCents = 0): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = Math.max(freq, 0.01);
  o.detune.value = detuneCents;
  return o;
}

/** Connect a list of nodes head to tail and return the tail. */
export function chain(...nodes: AudioNode[]): AudioNode {
  for (let i = 0; i < nodes.length - 1; i++) nodes[i]!.connect(nodes[i + 1]!);
  return nodes[nodes.length - 1]!;
}

/** Start/stop a source, swallowing double-stop errors from an already finished node. */
export function play(node: AudioScheduledSourceNode, start: number, stop: number): void {
  try {
    node.start(start);
  } catch {
    /* already started */
  }
  try {
    node.stop(stop);
  } catch {
    /* already stopped */
  }
}

export function stopSafe(node: AudioScheduledSourceNode, when: number): void {
  try {
    node.stop(when);
  } catch {
    /* already stopped */
  }
}

// ---------------------------------------------------------------------------
// Waveshaping
// ---------------------------------------------------------------------------

/**
 * `WaveShaperNode.curve` is typed against a non-shared ArrayBuffer in current lib.dom.
 * Deriving the alias from the DOM type itself keeps this correct across TypeScript versions.
 */
export type ShaperCurve = NonNullable<WaveShaperNode['curve']>;

/**
 * Soft saturation. `drive` 0 = clean, 1 = warm, 4+ = aggressive. Used to glue the gunshot
 * layers together and to make the drums hit.
 */
export function makeDistortionCurve(drive: number, samples = 2048): ShaperCurve {
  const c = new Float32Array(samples);
  const k = Math.max(drive, 0.0001) * 5;
  const norm = Math.tanh(k);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    c[i] = Math.tanh(k * x) / norm;
  }
  return c;
}

/**
 * A transparent brickwall-ish safety curve: perfectly linear below `knee`, saturating above,
 * hard-limited at 1. This is the last thing in the master chain — the game must never clip,
 * not even when three things explode at once.
 */
export function makeLimiterCurve(knee = 0.72, samples = 4096): ShaperCurve {
  const c = new Float32Array(samples);
  const head = 1 - knee;
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + head * Math.tanh((a - knee) / head);
    c[i] = Math.sign(x) * Math.min(y, 0.999);
  }
  return c;
}

/** Asymmetric shaper — adds even harmonics, which is what makes a sub-bass audible on laptop speakers. */
export function makeAsymCurve(drive = 1.6, samples = 2048): ShaperCurve {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    const y = x >= 0 ? Math.tanh(drive * x) : Math.tanh(drive * 0.62 * x);
    c[i] = clamp(y, -1, 1);
  }
  return c;
}

/** Wavefolder — bright, metallic, slightly unhinged. Used on the PULSE shimmer. */
export function makeFoldCurve(folds = 2.4, samples = 2048): ShaperCurve {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = ((i / (samples - 1)) * 2 - 1) * folds;
    c[i] = Math.sin(x * Math.PI * 0.5) * 0.9;
  }
  return c;
}

/**
 * Hard clipper with a short cubic knee. Unlike `tanh`, this keeps the fundamental at full
 * level and adds odd harmonics only to what actually exceeds the ceiling — which is what
 * makes an impact read as LOUD rather than as squashed. This is the curve on the impact
 * layers: on a laptop speaker the reproduced harmonics are the entire sound.
 */
export function makeCrunchCurve(drive = 2.2, samples = 4096): ShaperCurve {
  const c = new Float32Array(samples);
  const k = Math.max(drive, 0.2);
  for (let i = 0; i < samples; i++) {
    const x = ((i / (samples - 1)) * 2 - 1) * k;
    const a = Math.abs(x);
    // Cubic soft-knee up to 1, hard above — the classic guitar-amp transfer function.
    const y = a < 1 ? a - (a * a * a) / 3 : 0.6667;
    c[i] = (Math.sign(x) * y) / 0.6667;
  }
  return c;
}

/**
 * Sub harmonic exciter curve. Squares the input, which folds a sine at f into a DC-offset
 * tone at 2f. Feeding a 45 Hz sub through this and mixing the result back in puts real
 * energy at 90 and 180 Hz, so the weight of the hit survives on a speaker that reproduces
 * nothing below 150 Hz. The DC it generates is removed by `dcBlock`.
 */
export function makeExciterCurve(samples = 2048): ShaperCurve {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    // x^2 (2nd harmonic) plus a little |x|^3 with sign (3rd harmonic) for a fuller stack.
    c[i] = clamp(x * x * 1.6 - 0.5 + Math.sign(x) * Math.abs(x) * x * x * 0.5, -1, 1);
  }
  return c;
}

/**
 * DC blocker. Asymmetric saturation and the sub exciter both generate a DC offset, and DC
 * costs headroom without making any sound at all — a saturated sub without one of these is
 * a sub that quietly steals 20% of the limiter's range from the gunshot.
 */
export function dcBlock(ctx: BaseAudioContext, hz = 22): BiquadFilterNode {
  return biquad(ctx, 'highpass', hz, 0.6);
}

export function shaper(ctx: BaseAudioContext, curve: ShaperCurve, oversample: OverSampleType = '2x'): WaveShaperNode {
  const w = ctx.createWaveShaper();
  w.curve = curve;
  w.oversample = oversample;
  return w;
}

// ---------------------------------------------------------------------------
// Noise
// ---------------------------------------------------------------------------

export type NoiseKind = 'white' | 'pink' | 'brown' | 'velvet';

function fillNoise(out: Float32Array, kind: NoiseKind, rng: Rng): void {
  const n = out.length;
  switch (kind) {
    case 'white': {
      for (let i = 0; i < n; i++) out[i] = rng() * 2 - 1;
      break;
    }
    case 'pink': {
      // Paul Kellet's economy pink filter — flat enough for sound design, very cheap.
      let b0 = 0;
      let b1 = 0;
      let b2 = 0;
      let b3 = 0;
      let b4 = 0;
      let b5 = 0;
      let b6 = 0;
      for (let i = 0; i < n; i++) {
        const w = rng() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        out[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
      break;
    }
    case 'brown': {
      // Leaky integrator: -6 dB/oct. The body of every heavy impact in the game.
      let last = 0;
      for (let i = 0; i < n; i++) {
        const w = rng() * 2 - 1;
        last = (last + 0.022 * w) / 1.02;
        out[i] = last * 3.5;
      }
      break;
    }
    case 'velvet': {
      // Sparse signed impulses. Sounds "crisper" than white for scrapes and grit because it
      // has no low-level hiss floor between the transients.
      out.fill(0);
      const density = 2200; // impulses per second at 48k assumed; scaled below
      const stride = Math.max(2, Math.floor(48000 / density));
      for (let i = 0; i < n; i += stride) {
        const j = i + ((rng() * stride) | 0);
        if (j < n) out[j] = rng() < 0.5 ? -1 : 1;
      }
      break;
    }
  }
  // Normalise so every kind has comparable perceived level.
  let peak = 0;
  for (let i = 0; i < n; i++) {
    const a = Math.abs(out[i]!);
    if (a > peak) peak = a;
  }
  if (peak > 0.0001) {
    const s = 0.92 / peak;
    for (let i = 0; i < n; i++) out[i] *= s;
  }
}

/** Cache of generated noise buffers, keyed by kind + length + variant. */
export class NoiseFactory {
  private cache = new Map<string, AudioBuffer>();
  private readonly seed: number;

  constructor(
    private readonly ctx: BaseAudioContext,
    seed = 0x1a1d2e,
  ) {
    this.seed = seed >>> 0;
  }

  buffer(kind: NoiseKind, seconds: number, variant = 0): AudioBuffer {
    const samples = Math.max(128, Math.round(seconds * this.ctx.sampleRate));
    const key = `${kind}:${samples}:${variant}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    const buf = this.ctx.createBuffer(1, samples, this.ctx.sampleRate);
    fillNoise(buf.getChannelData(0), kind, makeRng((hashString(key) ^ this.seed) >>> 0));
    this.cache.set(key, buf);
    return buf;
  }

  /**
   * One-shot noise source, read from a pooled 2 second buffer. Start it with
   * `startAtRandomPhase` so repeated footsteps never sound like the same click twice.
   */
  source(kind: NoiseKind, playbackRate = 1): AudioBufferSourceNode {
    const pool = this.buffer(kind, 2.0);
    const src = this.ctx.createBufferSource();
    src.buffer = pool;
    src.playbackRate.value = playbackRate;
    src.loop = true;
    return src;
  }

  /** Looping noise for sustained textures (scrapes, tether hiss, wind). */
  loop(kind: NoiseKind, playbackRate = 1): AudioBufferSourceNode {
    const pool = this.buffer(kind, 3.0);
    const src = this.ctx.createBufferSource();
    src.buffer = pool;
    src.loop = true;
    src.playbackRate.value = playbackRate;
    return src;
  }

  dispose(): void {
    this.cache.clear();
  }
}

/** Start a looping buffer source at a random phase so repeats never sound identical. */
export function startAtRandomPhase(src: AudioBufferSourceNode, when: number, rng: Rng): void {
  const dur = src.buffer ? src.buffer.duration : 0;
  try {
    src.start(when, dur > 0.05 ? rng() * (dur - 0.05) : 0);
  } catch {
    /* already started */
  }
}

// ---------------------------------------------------------------------------
// Transient clicks
// ---------------------------------------------------------------------------

/**
 * A true transient: one or two samples of full-scale energy followed by a very short
 * exponential tail. This is what separates "a sound started" from "a sound happened" —
 * the gunshot, the rack and the dry click are all built on top of one of these.
 */
export class ClickFactory {
  private cache = new Map<string, AudioBuffer>();

  constructor(private readonly ctx: BaseAudioContext) {}

  /** `tailMs` shapes the body of the tick; `grit` mixes in noise instead of a pure impulse. */
  buffer(tailMs: number, grit: number, variant = 0): AudioBuffer {
    const key = `${tailMs.toFixed(2)}:${grit.toFixed(2)}:${variant}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    const sr = this.ctx.sampleRate;
    const n = Math.max(8, Math.round((tailMs / 1000) * sr));
    const buf = this.ctx.createBuffer(1, n, sr);
    const d = buf.getChannelData(0);
    const rng = makeRng((hashString('click' + key) >>> 0) || 7);
    const decay = 3.6 / n;
    for (let i = 0; i < n; i++) {
      const impulse = i === 0 ? 1 : 0;
      const noise = rng() * 2 - 1;
      d[i] = (impulse * (1 - grit) + noise * grit) * Math.exp(-i * decay * 6);
    }
    d[0] = 1;
    this.cache.set(key, buf);
    return buf;
  }

  source(tailMs: number, grit: number, variant = 0): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer(tailMs, grit, variant);
    return src;
  }

  dispose(): void {
    this.cache.clear();
  }
}

// ---------------------------------------------------------------------------
// Karplus-Strong — metal
// ---------------------------------------------------------------------------

export interface MetalOpts {
  /** Fundamental in Hz. */
  freq: number;
  /** Total render length in seconds. */
  seconds: number;
  /** 0..1 — how long the string rings. */
  sustain: number;
  /** 0..1 — how much high end survives the feedback loop. */
  brightness: number;
  /** 0..1 — inharmonic partial mix. 0 = a guitar string, 1 = a struck bar. */
  metal: number;
  variant?: number;
}

/**
 * Karplus-Strong rendered offline into a buffer.
 *
 * A real-time feedback loop through a DelayNode cannot go above ~340 Hz (the graph enforces
 * a one render quantum minimum delay in any cycle), and every metallic sound in this game
 * lives well above that. So we render the string by hand — it is a handful of microseconds
 * and the result is cached forever.
 */
export class MetalFactory {
  private cache = new Map<string, AudioBuffer>();

  constructor(private readonly ctx: BaseAudioContext) {}

  buffer(o: MetalOpts): AudioBuffer {
    const key = `${o.freq.toFixed(1)}:${o.seconds.toFixed(2)}:${o.sustain.toFixed(2)}:${o.brightness.toFixed(2)}:${o.metal.toFixed(2)}:${o.variant ?? 0}`;
    const hit = this.cache.get(key);
    if (hit) return hit;

    const sr = this.ctx.sampleRate;
    const n = Math.max(64, Math.round(o.seconds * sr));
    const buf = this.ctx.createBuffer(1, n, sr);
    const out = buf.getChannelData(0);
    const rng = makeRng((hashString('metal' + key) >>> 0) || 13);

    // Inharmonic partial ratios of a struck metal bar, blended toward harmonic as metal -> 0.
    const inharm = [1, 2.756, 5.404, 8.933];
    const harm = [1, 2, 3, 4];
    const weights = [1, 0.52, 0.3, 0.16];

    const damp = clamp01(1 - o.brightness) * 0.5 + 0.02; // one-pole loop damping
    const feedback = 0.9 + 0.0995 * clamp01(o.sustain);

    for (let p = 0; p < inharm.length; p++) {
      const r = lerpN(harm[p]!, inharm[p]!, clamp01(o.metal));
      const f = o.freq * r;
      if (f > sr * 0.45) continue;
      const len = Math.max(2, Math.round(sr / f));
      const line = new Float32Array(len);
      for (let i = 0; i < len; i++) line[i] = rng() * 2 - 1;

      // Excitation shaping: a brighter pluck for higher partials.
      let s = 0;
      const exciteLp = 0.25 + 0.6 * clamp01(o.brightness);
      for (let i = 0; i < len; i++) {
        s += (line[i]! - s) * exciteLp;
        line[i] = s;
      }

      const w = weights[p]! * (1 - 0.12 * p);
      const partialDamp = clamp01(damp * (1 + p * 0.55));
      const partialFb = Math.pow(feedback, 1 + p * 0.35);
      let idx = 0;
      let prev = 0;
      for (let i = 0; i < n; i++) {
        const cur = line[idx]!;
        const filtered = cur * (1 - partialDamp) + prev * partialDamp;
        prev = cur;
        line[idx] = filtered * partialFb;
        idx = idx + 1 >= len ? 0 : idx + 1;
        out[i] += cur * w;
      }
    }

    // Fade the tail so looping/cutting is never audible, and normalise.
    let peak = 0;
    for (let i = 0; i < n; i++) {
      const a = Math.abs(out[i]!);
      if (a > peak) peak = a;
    }
    const norm = peak > 0.0001 ? 0.9 / peak : 1;
    const fadeStart = Math.floor(n * 0.86);
    for (let i = 0; i < n; i++) {
      const f = i < fadeStart ? 1 : 1 - (i - fadeStart) / (n - fadeStart);
      out[i] *= norm * f * f;
    }

    this.cache.set(key, buf);
    return buf;
  }

  source(o: MetalOpts): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer(o);
    return src;
  }

  dispose(): void {
    this.cache.clear();
  }
}

// ---------------------------------------------------------------------------
// FM / ring modulation
// ---------------------------------------------------------------------------

export interface FmVoice {
  carrier: OscillatorNode;
  modulator: OscillatorNode;
  /** Gain node whose value IS the modulation index in Hz of deviation. */
  index: GainNode;
  output: GainNode;
  start(t: number): void;
  stop(t: number): void;
}

/**
 * Classic 2-operator FM. Bell-like ratios (1:1.41, 1:3.5) give the metallic timbres that
 * carry the hit markers and the heal chime.
 */
export function makeFm(
  ctx: BaseAudioContext,
  carrierHz: number,
  modRatio: number,
  indexHz: number,
  carrierType: OscillatorType = 'sine',
  modType: OscillatorType = 'sine',
): FmVoice {
  const carrier = osc(ctx, carrierType, carrierHz);
  const modulator = osc(ctx, modType, carrierHz * modRatio);
  const index = gain(ctx, indexHz);
  const output = gain(ctx, 1);
  modulator.connect(index);
  index.connect(carrier.frequency);
  carrier.connect(output);
  return {
    carrier,
    modulator,
    index,
    output,
    start(t: number) {
      try {
        carrier.start(t);
        modulator.start(t);
      } catch {
        /* already started */
      }
    },
    stop(t: number) {
      stopSafe(carrier, t);
      stopSafe(modulator, t);
    },
  };
}

export interface RingMod {
  input: GainNode;
  output: GainNode;
  oscillator: OscillatorNode;
  start(t: number): void;
  stop(t: number): void;
}

/**
 * Ring modulator. A GainNode with gain driven from -1..1 by an oscillator multiplies its
 * input, which is exactly ring modulation. Adds a clangorous, inorganic edge — used on the
 * PULSE and on the HIGH NOON stinger.
 */
export function makeRingMod(ctx: BaseAudioContext, freq: number, type: OscillatorType = 'sine'): RingMod {
  const input = gain(ctx, 1);
  const output = gain(ctx, 1);
  const modGain = gain(ctx, 0);
  const o = osc(ctx, type, freq);
  o.connect(modGain.gain);
  input.connect(modGain);
  modGain.connect(output);
  return {
    input,
    output,
    oscillator: o,
    start(t: number) {
      try {
        o.start(t);
      } catch {
        /* already started */
      }
    },
    stop(t: number) {
      stopSafe(o, t);
    },
  };
}

// ---------------------------------------------------------------------------
// LFO
// ---------------------------------------------------------------------------

export interface Lfo {
  oscillator: OscillatorNode;
  depth: GainNode;
  connectTo(param: AudioParam): void;
  start(t: number): void;
  stop(t: number): void;
}

/** A modulation source you can point at any AudioParam. `depth` is in the param's own units. */
export function makeLfo(
  ctx: BaseAudioContext,
  freq: number,
  depth: number,
  type: OscillatorType = 'sine',
  phaseOffsetSec = 0,
): Lfo {
  const o = osc(ctx, type, freq);
  const d = gain(ctx, depth);
  o.connect(d);
  return {
    oscillator: o,
    depth: d,
    connectTo(param: AudioParam) {
      d.connect(param);
    },
    start(t: number) {
      try {
        o.start(t + phaseOffsetSec);
      } catch {
        /* already started */
      }
    },
    stop(t: number) {
      stopSafe(o, t);
    },
  };
}

// ---------------------------------------------------------------------------
// Small utilities used across the sound design
// ---------------------------------------------------------------------------

/**
 * Air absorption: distant sounds lose high frequencies. Returns a lowpass tuned for
 * `metres`, or null when the source is close enough that filtering is a waste of a node.
 */
export function airAbsorption(ctx: BaseAudioContext, metres: number): BiquadFilterNode | null {
  if (metres < 8) return null;
  const cutoff = clamp(20000 * Math.exp(-metres / 46), 900, 19000);
  if (cutoff > 16000) return null;
  return biquad(ctx, 'lowpass', cutoff, 0.5);
}

/** Equal-power crossfade weight. */
export const equalPower = (t: number): number => Math.sin(clamp01(t) * Math.PI * 0.5);
